var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SessionError } from '@embed/errors';
import { isExpired } from '@embed/store/session/helpers';
import { defaultApiHost } from '@src/helpers';
import cookies from '@src/utils/cookies';
import { stringify, toObject } from '@src/utils/qs';
const apiHost = defaultApiHost;
const MIN_ACTIVITY = 1000 * 60 * 30;
export const sessionKey = '_fstptyfwvs';
const visitorIdTokenKey = 'vit';
const accessTokenKey = 'access_token';
const sessionCookieMaxDays = 60;
let lastActivity = new Date();
let sessionPromise = null;
let sessionStore = {};
export const getVisitorSession = (__sessionFetcher = fetchRemoteSession) => {
    let visitorIdToken = null;
    const locationQuery = toObject(window.location && window.location.search);
    try {
        if (window.location.pathname === '/pwa/oauth_callback' &&
            accessTokenKey in locationQuery) {
            // Oauth2 flow after being redirected here from Oauth provider
            const now = new Date();
            const expiresIn = locationQuery['expires_in'];
            const expiresAt = new Date(now.getTime() + expiresIn * 1000);
            const session = {
                token: locationQuery[accessTokenKey],
                expires_at: expiresAt,
                expires_in: locationQuery['expires_in'],
                session_id: locationQuery['session_id'],
                visitor_id: locationQuery['visitor_id'],
                // _visitor_id indicates an encoded visitor_id. In this case, an encoded user_id.
                // This is used for Pixel tracking purposes so we know to prefix _ to the field.
                _visitor_id: locationQuery['visitor_id'],
                // When logging in with OAuth, we know the resource is a User, and the
                // visitor_id is a user_id, so that can be stored in the session to
                // differentiate this from a Guest session.
                user_id: locationQuery['visitor_id'],
                // _user_id indicates an encoded user_id.
                // This is used for Pixel tracking purposes so we know to prefix _ to the field.
                // TODO: @deric remove user_id key when we know its no longer used
                _user_id: locationQuery['visitor_id'],
            };
            return validateAndResolve(Object.assign(Object.assign({}, session), { session: Object.assign({}, session) }));
        }
        // Tries to return session from localStorage. Fails if there's no session in there or if
        // it's expired
        const session = getSessionFromLocalStorage();
        const { expires_at } = session || {};
        if (session) {
            return validateAndResolve(session);
        }
        else if (accessTokenKey in locationQuery) {
            // Tries to fetch an access_token from the URL (covers the case for iframes on ITP)
            // TODO: what about refresh token and session id
            return validateAndResolve({
                access_token: locationQuery[accessTokenKey],
                expires_at: locationQuery[expires_at],
            });
        }
        throw new SessionError('Missing session');
    }
    catch (error) {
        if (error instanceof SessionError) {
            if (sessionPromise) {
                return sessionPromise;
            }
            else {
                clearSession();
                if (visitorIdTokenKey in locationQuery) {
                    // Tries to fetch a new session on the API using temp token (visitor_id_token).
                    // Resolve temp token from url.
                    visitorIdToken = locationQuery[visitorIdTokenKey];
                }
                sessionPromise = __sessionFetcher({
                    visitorIdToken,
                }).then((session) => {
                    sessionPromise = null;
                    lastActivity = new Date();
                    return Promise.resolve(session);
                });
                return sessionPromise;
            }
        }
        else {
            throw error;
        }
    }
};
export const fetchRemoteSession = ({ visitorIdToken }) => __awaiter(void 0, void 0, void 0, function* () {
    let session = undefined;
    try {
        const url = `${apiHost}/api/sessions?${stringify({
            vit: visitorIdToken,
            provider: 'fw_visitor',
        })}`;
        const response = yield fetch(url, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (response && response.status >= 200 && response.status < 300) {
            const responseObj = yield response.json();
            session = responseObj.session;
        }
        else {
            throw new SessionError(`Invalid response (${response && response.status})`);
        }
    }
    catch (error) {
        if (error instanceof TypeError) {
            // Bypass sentry for CORS related errors (failed to fetch)
            throw new SessionError(error);
        }
        if (error.message === 'Timeout') {
            throw new SessionError('Session timeout');
        }
        throw error;
    }
    // Modify expires_at to reflect possible user's
    // date & time settings mismatch with server
    const { expires_in } = session;
    const now = new Date();
    const expires_at = new Date(now.getTime() + expires_in * 1000);
    session = { session: Object.assign(Object.assign({}, session), { expires_at }) };
    setSession(session);
    return session;
});
export const getSessionFromLocalStorage = () => {
    try {
        const sessionFromStorage = JSON.parse(window.localStorage && window.localStorage.getItem(sessionKey));
        const sessionFromCookies = JSON.parse(cookies.get(sessionKey));
        return sessionFromCookies || sessionFromStorage;
    }
    catch (error) {
        return sessionStore;
    }
};
export const validateAndResolve = (session) => {
    const { session: { expires_at } = {} } = session || {};
    if (!expires_at || isExpired(expires_at)) {
        throw new SessionError('Expired session token');
    }
    if (lastActivity && new Date() - lastActivity > MIN_ACTIVITY) {
        // Throw to request session refresh
        throw new SessionError('Inactivity');
    }
    setSession(session);
    return Promise.resolve(session);
};
export const setSession = (newSession) => {
    try {
        sessionStore = newSession;
        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + sessionCookieMaxDays);
        cookies.set(sessionKey, JSON.stringify(newSession), expireDate);
        window.localStorage &&
            window.localStorage.setItem(sessionKey, JSON.stringify(newSession));
    }
    catch (error) {
        // Pass silently
    }
};
export const clearSession = () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        sessionStore = {};
        window.localStorage && window.localStorage.clear(sessionKey);
        cookies.remove(sessionKey);
    }
    catch (error) {
        // Pass silently
    }
});
export const lastActivitySetter = () => {
    lastActivity = new Date();
};
